"use client";



import Image from "@/components/shared/image";

import Link from "@/components/shared/link";

import { useEffect, useRef, useState, type ReactElement } from "react";

import { HugeiconsIcon } from "@hugeicons/react";

import ArrowDown01Icon from "@hugeicons/core-free-icons/ArrowDown01Icon";

import ColorPickerIcon from "@hugeicons/core-free-icons/ColorPickerIcon";

import Moon01Icon from "@hugeicons/core-free-icons/Moon01Icon";

import Sun01Icon from "@hugeicons/core-free-icons/Sun01Icon";
import Menu02Icon from "@hugeicons/core-free-icons/Menu02Icon";

import { useAuth } from "@/components/auth/AuthProvider";

import { useColorTheme } from "@/components/providers/color-theme-provider";

import { useTheme } from "@/components/providers/theme-provider";

// import { NavbarLanguageSelect } from "@/components/NavbarLanguageSelect";

import { ThemeToggle } from "@/components/shared/theme-toggle";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import { Button } from "@/components/ui/button";

import {

  Drawer,

  DrawerContent,

  DrawerFooter,

  DrawerHeader,

  DrawerTitle,

  DrawerTrigger,

} from "@/components/ui/drawer";

import { NavbarAnnouncements } from "@/components/NavbarAnnouncements";

import {

  AbacusSolidRoundedIcon,

  DashboardSquare02BulkRoundedIcon,

  GiftCardSolidRoundedIcon,

  Home01SolidRoundedIcon,

  Mail01SolidRoundedIcon,

  Megaphone01SolidRoundedIcon,

  MoneyBag02SolidRoundedIcon,

  News01SolidRoundedIcon,

  ToolCaseSolidRoundedIcon,

  CompareSolidRoundedIcon,

} from "@/lib/custom-icons";

import { useAnalytics } from "@/lib/analytics";

import { authClient } from "@/lib/better-auth";

import { siteContent, type AccentTheme } from "@/lib/site-content";

import { cn } from "@/lib/utils";



const DASHBOARD_AVATAR = "/creators/creator-9.webp";

const LIGHT_LOGO = "/logo.webp";

const DARK_LOGO = "/assets/dark_logo_2.png";



const SCROLL_THRESHOLD = 12;

type NavIconProps = { size?: number; color?: string; className?: string };

type NavItem = {

  label: string;

  href: string;

  icon: (props: NavIconProps) => ReactElement;

};



const MAIN_LINKS: NavItem[] = [

  { label: "Home", href: "/", icon: Home01SolidRoundedIcon },

  { label: "Pricing", href: "/pricing", icon: MoneyBag02SolidRoundedIcon },

  { label: "Blogs", href: "/blog", icon: News01SolidRoundedIcon },

  { label: "Contact", href: "/contact", icon: Mail01SolidRoundedIcon },

] as const;

const MORE_LINKS: NavItem[] = [

  { label: "Free tools", href: "/tools", icon: ToolCaseSolidRoundedIcon },

  { label: "Use cases", href: "/use-case", icon: AbacusSolidRoundedIcon },

  { label: "Referral", href: "/referral", icon: GiftCardSolidRoundedIcon },

] as const;



const SOLUTION_LINKS: NavItem[] = [

  ...MORE_LINKS,

  { label: "Comparisons", href: "/comparison", icon: CompareSolidRoundedIcon },

];

const ACCENT_OPTIONS: { key: AccentTheme; label: string; className: string }[] = [

  { key: "zapify", label: "Zapify", className: "bg-[linear-gradient(90deg,#ff8c42,#ff4e8e,#a06af9)]" },

  { key: "blue", label: "Blue", className: "bg-[#2563eb]" },

  { key: "orange", label: "Orange", className: "bg-[#f97316]" },

  { key: "green", label: "Green", className: "bg-[#22c55e]" },

  { key: "yellow", label: "Yellow", className: "bg-[#facc15]" },

  { key: "red", label: "Red", className: "bg-[#ef4444]" },

];



/* ─── active-link by pathname ─── */

function useActiveLink() {

  const [pathname, setPathname] = useState("/");



  useEffect(() => {

    setPathname(window.location.pathname);

    const update = () => setPathname(window.location.pathname);

    window.addEventListener("popstate", update);

    return () => window.removeEventListener("popstate", update);

  }, []);



  return pathname;

}



/* ─── nav links pill ─── */

function NavLinks() {

  const pathname = useActiveLink();

  const [moreOpen, setMoreOpen] = useState(false);

  const moreRef = useRef<HTMLDivElement>(null);

  const linkClass = (href: string) =>

    cn(

      "group inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-full px-3 text-sm leading-none transition-colors select-none",

      (href === "/" ? pathname === "/" : pathname.startsWith(href))

        ? "bg-white font-semibold text-foreground shadow-sm dark:bg-neutral-800"

        : "font-medium text-muted-foreground hover:text-foreground"

    );



  useEffect(() => {

    const onPointerDown = (event: MouseEvent) => {

      const target = event.target as Node;

      if (moreRef.current && !moreRef.current.contains(target)) setMoreOpen(false);

    };

    document.addEventListener("mousedown", onPointerDown);

    return () => document.removeEventListener("mousedown", onPointerDown);

  }, []);



  return (

    <div

      className="relative z-40 flex w-max min-w-0 max-w-full items-center gap-0.5 rounded-full bg-black/[0.04] px-1 py-1 dark:bg-white/[0.06]"

      role="navigation"

      aria-label="Page sections"

    >

      {MAIN_LINKS.map((link) => (

        <Link key={link.href} href={link.href} className={linkClass(link.href)}>

          <span className="mr-1.5 inline-flex items-center justify-center min-[900px]:hidden">

            <link.icon

              size={18}

              color="currentColor"

              className="block text-primary drop-shadow-[0_1px_0_rgba(0,0,0,0.08)] transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:scale-110"

            />

          </span>

          {link.label}

        </Link>

      ))}

      <div ref={moreRef} className="relative">

        <button

          type="button"

          onClick={() => setMoreOpen((prev) => !prev)}

          className={cn(

            "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm leading-none transition-colors select-none",

            "font-medium text-muted-foreground hover:text-foreground"

          )}

          aria-haspopup="menu"

          aria-expanded={moreOpen}

        >

          Solution

          <HugeiconsIcon

            icon={ArrowDown01Icon}

            size={15}

            color="currentColor"

            className={cn("transition-transform duration-200", moreOpen ? "rotate-180" : "rotate-0")}

          />

        </button>

        {moreOpen ? (

          <div className="absolute right-0 top-[calc(100%+8px)] z-50 min-w-[12rem] rounded-lg border border-border bg-white p-1.5 dark:bg-black">

            {SOLUTION_LINKS.map((item) => (

              <Link

                key={item.href}

                href={item.href}

                className="group flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/60"

                onClick={() => setMoreOpen(false)}

              >

                <item.icon

                  size={19}

                  color="currentColor"

                  className="shrink-0 text-primary transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:scale-110"

                />

                {item.label}

              </Link>

            ))}

          </div>

        ) : null}

      </div>

    </div>

  );

}



/* ─── main navbar ─── */

export default function Navbar() {

  const [scrolled, setScrolled] = useState(false);

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const [accentDrawerOpen, setAccentDrawerOpen] = useState(false);

  const { openAuth, isAuthenticated } = useAuth();

  const { track } = useAnalytics();

  const { data: authSession } = authClient.useSession();

  const { resolvedTheme, setTheme } = useTheme();

  const { themeColor, setThemeColor, previewThemeColor } = useColorTheme();

  const logoSrc = resolvedTheme === "dark" ? DARK_LOGO : LIGHT_LOGO;

  const sessionUser = authSession?.user;

  const isDashboardCta = isAuthenticated || Boolean(sessionUser);

  const dashboardAvatarName = sessionUser?.name?.trim() || "Dashboard";

  const dashboardAvatarFallback = sessionUser?.name?.trim().charAt(0).toUpperCase() || "Z";

  useEffect(() => {

    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);

    onScroll();

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);

  }, []);



  const ctaLabel = isDashboardCta ? "Dashboard" : siteContent.hero.ctaPrimary;



  const handleDashboardClick = () => {

    const ctaId = window.matchMedia("(min-width: 900px)").matches

      ? "CTA_Header_Desktop"

      : "CTA_Header_Mobile";



    track("cta_clicked", {

      cta_id: ctaId,

      button_text: ctaLabel,

    });

    openAuth();

  };



  return (

    /*

     * Header wrapper: always fully transparent — never carries its own background.

     * At top    : card is off-white + inset shadow only (no drop-shadow outside).

     * On scroll : card becomes pure white + drop-shadow below it.

     */

    <header className="fixed inset-x-0 top-0 z-50 w-full">

      <div className="mx-auto max-w-7xl px-3 pt-[max(env(safe-area-inset-top),1rem)] pb-2 sm:px-4 sm:pt-5 sm:pb-2.5 md:px-6 md:pt-6 md:pb-3">

        {/* ← This card is the only element with a background ← */}

        <div

          className={cn(

            "overflow-visible rounded-2xl border transition-all duration-300",

            scrolled

              ? "border-border/50 bg-white shadow-[0_2px_10px_0_rgba(0,0,0,0.07)] dark:bg-[#0a0a0a] dark:shadow-[0_2px_10px_0_rgba(0,0,0,0.60)]"

              : "border-border/0 "

          )}

        >

          <div className="flex flex-col gap-2 px-2 py-2 sm:px-3 sm:py-2.5">



            {/* Top row: logo · links (≥900) · actions

                Desktop: 1fr / auto / 1fr so the nav pill is centered in the bar (not in the gap

                between uneven left vs right widths — e.g. after adding language + theme + CTA). */}

            <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 overflow-visible min-[900px]:grid-cols-[1fr_auto_1fr] min-[900px]:gap-4">



              {/* Logo area */}

              <div className="flex h-10 min-w-0 shrink-0 items-center gap-2 min-[900px]:justify-self-start">

                <div className="min-[900px]:hidden">

                  <Drawer direction="left" open={mobileDrawerOpen} onOpenChange={setMobileDrawerOpen}>

                    <DrawerTrigger asChild>

                      <button

                        type="button"

                        aria-label="Open menu"

                        className="flex size-10 shrink-0 items-center justify-center text-foreground/90 transition-colors hover:text-foreground"

                      >

                        <HugeiconsIcon icon={Menu02Icon} size={22} color="currentColor" />

                      </button>

                    </DrawerTrigger>

                    <DrawerContent className="w-[68vw] max-w-[260px] bg-background/80 backdrop-blur-md">

                      <DrawerHeader className="border-b border-border/70 pb-2.5">

                        <Link href="#hero" className="inline-flex items-center" aria-label="Zapify home">

                          <Image

                            src={logoSrc}

                            alt="Zapify"

                            width={108}

                            height={34}

                            className="h-7 w-auto max-w-full object-contain"

                            style={{ maxWidth: '108px', maxHeight: '34px' }}

                            sizes="108px"

                            srcSet={logoSrc === LIGHT_LOGO ? "/logo_92w.webp 92w, /logo_184w.webp 184w, /logo.webp 228w" : undefined}

                          />

                        </Link>

                        <DrawerTitle className="sr-only">Navigation</DrawerTitle>

                      </DrawerHeader>



                      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-2.5">

                        <section>

                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Menu</p>

                          <div className="space-y-1.5">

                            {MAIN_LINKS.map((link) => (

                              <Link

                                key={link.href}

                                href={link.href}

                                onClick={() => setMobileDrawerOpen(false)}

                                className="group flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-foreground hover:bg-muted/60"

                              >

                                <link.icon

                                  size={20}

                                  color="currentColor"

                                  className="shrink-0 text-primary transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:scale-110"

                                />

                                {link.label}

                              </Link>

                            ))}

                          </div>

                        </section>



                        <section className="pb-1">

                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Solution</p>

                          <div className="space-y-1.5">

                            {SOLUTION_LINKS.map((link) => (

                              <Link

                                key={link.href}

                                href={link.href}

                                onClick={() => setMobileDrawerOpen(false)}

                                className="group flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-foreground hover:bg-muted/60"

                              >

                                <link.icon

                                  size={20}

                                  color="currentColor"

                                  className="shrink-0 text-primary transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:scale-110"

                                />

                                {link.label}

                              </Link>

                            ))}

                          </div>

                        </section>

                      </div>



                      <DrawerFooter className="border-t border-border/70 pt-2.5">

                        <div className="flex items-center justify-between gap-2">

                          <div className="flex items-center gap-2">

                            <Button

                              type="button"

                              variant="outline"

                              size="icon"

                              className="size-10 rounded-full bg-background/75 backdrop-blur-md"

                              aria-label={resolvedTheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}

                              onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}

                            >

                              {resolvedTheme === "dark" ? (

                                <HugeiconsIcon

                                  icon={Sun01Icon}

                                  size={17}

                                  color="currentColor"

                                  className="text-primary"

                                  aria-hidden

                                />

                              ) : (

                                <HugeiconsIcon icon={Moon01Icon} size={17} color="currentColor" className="text-primary" aria-hidden />

                              )}

                            </Button>

                            <Button

                              type="button"

                              variant="outline"

                              size="icon"

                              className="size-10 rounded-full bg-background/75 backdrop-blur-md"

                              aria-label="Open theme color selector"

                              onClick={() => {

                                setMobileDrawerOpen(false);

                                window.setTimeout(() => {

                                  setAccentDrawerOpen(true);

                                }, 180);

                              }}

                            >

                              <HugeiconsIcon icon={ColorPickerIcon} size={17} color="currentColor" aria-hidden />

                            </Button>

                            {/* <NavbarLanguageSelect variant="mobile-bottom" /> */}

                          </div>

                          <p className="text-right text-sm leading-tight font-semibold text-foreground">

                            Built with <span className="text-rose-500">❤</span>

                            <br />

                            for creators

                          </p>

                        </div>

                        <p className="text-center text-[11px] leading-relaxed text-muted-foreground">

                          <Link href="/privacy" onClick={() => setMobileDrawerOpen(false)} className="hover:text-foreground">

                            Privacy Policy

                          </Link>

                          {" · "}

                          <Link href="/terms" onClick={() => setMobileDrawerOpen(false)} className="hover:text-foreground">

                            Terms

                          </Link>

                          {" · "}

                          <Link href="/refund-policy" onClick={() => setMobileDrawerOpen(false)} className="hover:text-foreground">

                            Refund

                          </Link>

                          {" · "}

                          <Link href="/contact" onClick={() => setMobileDrawerOpen(false)} className="hover:text-foreground">

                            Contact

                          </Link>

                        </p>

                      </DrawerFooter>

                    </DrawerContent>

                  </Drawer>

                </div>

                <Link href="/" aria-label="Zapify home" className="flex items-center">

                  <Image

                    src={logoSrc}

                    alt="Zapify"

                    width={88}

                    height={28}

                    className="h-6 w-auto max-w-[92px] object-contain min-[900px]:h-9 min-[900px]:max-w-[200px]"

                    fetchPriority="high"

                    loading="eager"

                    style={{ maxWidth: '88px', maxHeight: '28px' }}

                    sizes="(max-width: 899px) 92px, 200px"

                    srcSet={logoSrc === LIGHT_LOGO ? "/logo_92w.webp 92w, /logo_184w.webp 184w, /logo.webp 228w" : undefined}

                  />

                </Link>

              </div>



              {/* Centre links — hidden below 900px; desktop: optical center (1fr auto 1fr parent) */}

              <div className="hidden min-w-0 min-[900px]:flex min-[900px]:justify-self-center">

                <NavLinks />

              </div>



              {/* Right actions */}

              <div className="flex min-w-0 items-center justify-end justify-self-end gap-2 overflow-visible">

                <div className="hidden min-[900px]:block">

                  {/* <NavbarLanguageSelect /> */}

                </div>

                <div className="hidden min-[900px]:block">

                  <ThemeToggle />

                </div>

                <Button

                  type="button"

                  variant="default"

                  className={cn(

                    "h-10 gap-2 rounded-full text-sm font-bold shadow-sm dark:text-black",

                    themeColor === "yellow" && "!text-black",

                    isDashboardCta ? "pl-1 pr-3 sm:pr-4" : "px-4 sm:px-5"

                  )}

                  onClick={handleDashboardClick}

                >

                  {isDashboardCta ? (

                    <Avatar className="size-7 border-2 border-primary-foreground/20">

                      <AvatarImage src={sessionUser?.image ?? undefined} alt={dashboardAvatarName} />

                      <AvatarFallback className="bg-primary-foreground/15 text-xs text-primary-foreground">

                        {dashboardAvatarFallback}

                      </AvatarFallback>

                    </Avatar>

                  ) : null}

                  <span>{ctaLabel}</span>

                </Button>

                {/* <NavbarAnnouncements onLoginCta={() => openAuth()} /> */}

              </div>

            </div>

            <div className="min-[900px]:hidden">

              <Drawer direction="bottom" open={accentDrawerOpen} onOpenChange={setAccentDrawerOpen}>

                <DrawerContent>

                  <div className="px-3 pb-3 pt-1">

                    <DrawerTitle className="text-sm">Accent color</DrawerTitle>

                    <p className="mb-1.5 mt-1 text-[10px] leading-snug text-muted-foreground">

                      Hover swatches to preview; click to save. Use the sun/moon button for light or dark.

                    </p>

                    <div

                      className="flex flex-nowrap items-center justify-center gap-1 rounded-lg border border-border/70 bg-muted/20 px-1.5 py-1.5"

                      onMouseLeave={() => previewThemeColor(null)}

                    >

                      {ACCENT_OPTIONS.map((accent) => (

                        <button

                          key={accent.key}

                          type="button"

                          title={accent.label}

                          aria-label={`${accent.label} accent`}

                          onMouseEnter={() => previewThemeColor(accent.key)}

                          onClick={() => {

                            setThemeColor(accent.key, "navbar_mobile_accent_drawer");

                            setAccentDrawerOpen(false);

                          }}

                          className={cn(

                            "flex size-5 shrink-0 items-center justify-center rounded-full border transition-all",

                            themeColor === accent.key

                              ? "scale-105 border-foreground/70 ring-2 ring-primary/30"

                              : "border-border/70 hover:scale-105 hover:border-foreground/30"

                          )}

                        >

                          <span

                            className={cn(

                              "size-2.5 rounded-full ring-1 ring-black/10 dark:ring-white/15",

                              accent.className

                            )}

                          />

                        </button>

                      ))}

                    </div>

                  </div>

                </DrawerContent>

              </Drawer>

            </div>



          </div>

        </div>

      </div>

    </header>

  );

}

